import { registerInputs } from '../functions/registerInput';
import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import { FormHeaderButton } from '../components/FormHeaderButton';
import { useForm } from 'react-hook-form';
import { MODES, useMergeContext } from '../../../store/mergeContext';
import { Fragment, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Trans, msg, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { resolveConfigDisplayLabel, resolveOptionDisplayLabel } from '../../../utils/config-localization';
import {
  formatOrganizationFieldValue,
  hasCrashRoleData,
  isOrganizationFieldKey,
} from '../merge-constants';
import { formatDateForList } from '../../../utils';
import {
  buildMergeRegisterName,
  buildConfigSelectOptions,
  normalizeMergedForForm,
  resolveSelectFormValue,
  restoreMergedPayloadTypes,
  toSelectFormString,
} from '../merge-form-select';
import {
  buildFieldOwnerRoleMap,
  buildMergedSectionDefaults,
  getNestedMergeValue,
  prepareMergeFormInputPaths,
  resolveMergedFieldDefault,
} from '../merge-form-utils';
import { useMergedMergeConfiguration } from '../use-merged-merge-configuration';

const buildRegisterName = buildMergeRegisterName;

const isRoleActiveInMerge = (role, mergeState) =>
  hasCrashRoleData(mergeState?.[role]);

const isDateFieldKey = (fieldKey) =>
  fieldKey === 'Crash date' ||
  fieldKey === 'Report date' ||
  /\bdate\b/i.test(String(fieldKey));

const formatFieldValue = (value, fieldKey) => {
  if (value == null || value === '') return '';
  if (isDateFieldKey(fieldKey)) return formatDateForList(value);
  return value;
};

const resolveFieldValue = (rawValue, configField) => {
  if (configField?.options?.length) {
    return resolveSelectFormValue(rawValue, configField);
  }
  if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
    return rawValue;
  }
  return rawValue ?? '';
};

const DEFAULT_HEADERS = ['Value', 'Police', 'Hospital', 'Insurance'];
const MERGE_FORM_COLUMNS = 5;
const KEYS_TO_IGNORE = ['unitIndex', 'personIndex'];

const mergeFormGridSx = {
  display: 'grid',
  gridTemplateColumns: `repeat(${MERGE_FORM_COLUMNS}, minmax(0, 1fr))`,
  width: '100%',
};

const mergeFormCellSx = {
  minWidth: 0,
  px: 1.5,
  py: 1.25,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const mergeFieldInputSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    fontSize: 13,
    borderRadius: '8px',
    backgroundColor: 'var(--color-surface, #fff)',
  },
};

const FUNCTIONS_TO_BUILD = ['police', 'hospital', 'insurance'];

const prepareRow = (
  inputs,
  mergeState,
  registerInputsFn,
  control,
  config,
  theme,
  resolveFieldLabel,
  formatOrganizationLabel,
  resolveSelectLabel,
  fieldOwnerRoleMap,
) => {
  const EmptySourceCell = () => (
    <Typography
      variant="caption"
      sx={{
        color: 'text.disabled',
        fontStyle: 'italic',
        fontWeight: 500,
      }}
    >
      <Trans>Not available</Trans>
    </Typography>
  );

  const getConfigField = (fieldKey) => config?.[fieldKey];

  const buildFieldOptions = (fieldKey, rawValue) => {
    const configField = getConfigField(fieldKey);

    const formatOptionLabel = (value, fallbackLabel, optionItem) => {
      if (isOrganizationFieldKey(fieldKey)) {
        return formatOrganizationLabel(value);
      }
      if (optionItem) {
        return resolveOptionDisplayLabel(optionItem) || fallbackLabel || String(value ?? '');
      }
      return resolveSelectLabel(fieldKey, value, fallbackLabel);
    };

    if (configField?.options?.length) {
      return buildConfigSelectOptions(configField, formatOptionLabel, rawValue);
    }

    if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
      const label = isDateFieldKey(fieldKey)
        ? formatFieldValue(rawValue, fieldKey)
        : formatOptionLabel(rawValue, String(rawValue));
      return {
        value: String(rawValue),
        label,
      };
    }

    return [];
  };

  const getFieldFormatters = (fieldKey) => {
    if (isOrganizationFieldKey(fieldKey)) {
      return { formatDisplayValue: formatOrganizationLabel };
    }
    if (isDateFieldKey(fieldKey)) {
      return {
        formatDisplayValue: (value) => formatFieldValue(value, fieldKey),
      };
    }
    const configField = getConfigField(fieldKey);
    if (configField?.options?.length) {
      return {
        formatDisplayValue: (value) =>
          resolveSelectLabel(fieldKey, value, String(value ?? '')),
      };
    }
    return {};
  };

  return (
    <Fragment>
      {inputs?.map((input, rowIndex) => {
        const valuesToCheck = input?.split('.');
        const fieldKey = valuesToCheck?.[valuesToCheck.length - 1] ?? input;
        const fieldLabel = resolveFieldLabel(fieldKey);
        const configField = getConfigField(fieldKey);
        const fieldFormatters = getFieldFormatters(fieldKey);
        const isSelectField = Boolean(configField?.options?.length);

        const myInputs = FUNCTIONS_TO_BUILD?.map((f) => {
          const roleData = mergeState?.[f];
          const rawValue = getNestedMergeValue(roleData, valuesToCheck);
          const fieldValue = resolveFieldValue(rawValue, configField);
          const displayValue = isSelectField
            ? fieldValue
            : formatFieldValue(fieldValue, fieldKey);
          const fieldOptions = buildFieldOptions(fieldKey, fieldValue);
          const registerName = buildRegisterName(f, valuesToCheck);

          if (!isRoleActiveInMerge(f, mergeState)) {
            return (
              <Box
                key={`empty-${f}-${input}`}
                sx={{
                  ...mergeFormCellSx,
                  justifyContent: 'center',
                }}
              >
                <EmptySourceCell />
              </Box>
            );
          }

          return registerInputsFn({
            inputs: [
              {
                registerName,
                fieldValue: displayValue,
                options: fieldOptions,
                type: isSelectField ? 'select' : '',
                readOnly: true,
                inputSx: mergeFieldInputSx,
                ...fieldFormatters,
              },
            ],
            control,
          });
        });

        const mapOptions = [
          ...FUNCTIONS_TO_BUILD?.flatMap((f) => {
            if (!isRoleActiveInMerge(f, mergeState)) return [];
            const rawValue = getNestedMergeValue(mergeState?.[f], valuesToCheck);
            const fieldValue = resolveFieldValue(rawValue, configField);
            const opts = buildFieldOptions(fieldKey, fieldValue);
            return Array.isArray(opts) ? opts : [opts];
          }),
          { value: '', label: '' },
        ];

        const uniqueId = [
          ...new Set(
            mapOptions?.flatMap((opt) =>
              Array.isArray(opt)
                ? opt?.flatMap((subOpt) => subOpt)
                : opt?.value,
            ),
          ),
        ];

        const uniqueOptions = uniqueId
          ?.map((id) =>
            mapOptions?.find((opt) => String(opt?.value) === String(id)),
          )
          .filter(Boolean)
          .map((opt) => ({
            ...opt,
            value: toSelectFormString(opt?.value),
          }));

        const mergedRawValue = resolveMergedFieldDefault(
          valuesToCheck,
          mergeState,
          fieldOwnerRoleMap,
          fieldKey,
        );
        const mergedFieldValue = resolveFieldValue(mergedRawValue, configField);

        const mergeInput = {
          registerName: buildRegisterName('merged', valuesToCheck),
          defaultValue: mergedFieldValue,
          fieldValue: isSelectField ? mergedFieldValue : formatFieldValue(mergedFieldValue, fieldKey),
          type: isSelectField || uniqueOptions?.some((opt) => opt?.value !== opt?.label)
            ? 'select'
            : 'autocomplete',
          options: isSelectField ? buildFieldOptions(fieldKey, mergedFieldValue) : uniqueOptions,
          inputSx: mergeFieldInputSx,
          ...fieldFormatters,
        };

        const isEven = rowIndex % 2 === 0;

        return (
          <Box
            key={input}
            sx={{
              ...mergeFormGridSx,
              borderBottom: `1px solid ${theme.palette.divider}`,
              bgcolor: isEven
                ? (theme.palette.mode === 'light' ? '#f8fafc' : 'rgba(255,255,255,0.03)')
                : 'transparent',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                ...mergeFormCellSx,
                justifyContent: 'flex-start',
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: theme.palette.newColors?.gray || theme.palette.text.primary,
                }}
              >
                {fieldLabel}
              </Typography>
            </Box>
            {myInputs}
            {registerInputsFn({
              inputs: [mergeInput],
              control,
            })}
          </Box>
        );
      })}
    </Fragment>
  );
};

const MergeFormFields = ({
  mergeState,
  configuration,
  configurationMap,
  theme,
  resolveFieldLabel,
  formatOrganizationLabel,
  resolveSelectLabel,
  onSubmit,
}) => {
  const fieldOwnerRoleMap = useMemo(
    () => buildFieldOwnerRoleMap(configurationMap),
    [configurationMap],
  );

  const formDefaults = useMemo(() => ({
    ...mergeState,
    merged: normalizeMergedForForm(
      buildMergedSectionDefaults(mergeState, configurationMap, configuration),
      configurationMap,
    ),
  }), [mergeState, configurationMap, configuration]);

  const { handleSubmit, control } = useForm({
    defaultValues: formDefaults,
  });

  const inputs = useMemo(
    () => prepareMergeFormInputPaths(mergeState, configuration),
    [mergeState, configuration],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {prepareRow(
        inputs,
        mergeState,
        registerInputs,
        control,
        configurationMap,
        theme,
        resolveFieldLabel,
        formatOrganizationLabel,
        resolveSelectLabel,
        fieldOwnerRoleMap,
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
          px: 2,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === 'light' ? '#f8fafc' : 'rgba(255,255,255,0.03)',
        }}
      >
        <Button
          type="submit"
          variant="contained"
          sx={{
            background: theme.palette.newColors?.pinkGradient,
            color: theme.palette.common.white,
            fontWeight: 700,
            fontSize: 13,
            textTransform: 'none',
            px: 3,
            borderRadius: '10px',
            boxShadow: 'none',
            '&:hover': {
              background: theme.palette.newColors?.pinkGradient,
              opacity: 0.92,
              boxShadow: 'none',
            },
          }}
        >
          <Trans>Save section</Trans>
        </Button>
      </Box>
    </form>
  );
};

export const MergeForm = () => {
  const { _ } = useLingui();
  const theme = useTheme();
  const MERGE_LABEL = _(msg`Merged`);
  const VALIDATE_LABEL = _(msg`Validated`);
  const { mergeState, updateSelectedCrashDetail, mode } = useMergeContext();
  const mergeSectionKey = `${mergeState?.type ?? ''}:${mergeState?.index ?? mergeState?.personKey ?? 'none'}`;

  const location = useLocation();
  const token = location.state?.token || null;
  const init = useMemo(
    () => location.state?.init || {},
    [location.state?.init],
  );
  const organizations = useMemo(
    () => init?.organizations || [],
    [init?.organizations],
  );
  const organizationFallbackLabels = useMemo(
    () => ({
      police: t`Police`,
      hospital: t`Hospital`,
      insurance: t`Insurance`,
    }),
    [],
  );
  const formatOrganizationLabel = useMemo(
    () => (value) =>
      formatOrganizationFieldValue(value, organizations, organizationFallbackLabels),
    [organizations, organizationFallbackLabels],
  );
  const { mergedConfiguration } = useMergedMergeConfiguration(
    token,
    init?.configuration,
  );
  const inputConfiguration = useMemo(
    () => mergedConfiguration ?? init?.configuration,
    [mergedConfiguration, init?.configuration],
  );
  const flatConfiguration = useMemo(
    () =>
      Object.values(inputConfiguration || {})?.flatMap((conf) =>
        conf?.elements?.flatMap((ele) => ele),
      ),
    [inputConfiguration],
  );
  const configurationMap = useMemo(
    () =>
      flatConfiguration?.reduce(
        (acc, val) => ({ ...acc, [val?.label]: { ...val } }),
        {},
      ),
    [flatConfiguration],
  );

  const onSubmit = (values) => {
    const mergedPayload = restoreMergedPayloadTypes(
      { ...values.merged, done: true },
      configurationMap,
    );
    updateSelectedCrashDetail({
      type: values.type,
      data: mergedPayload,
      ...(values.index !== undefined && values.index !== null && { index: values.index }),
      ...(values.personKey ? { personKey: values.personKey } : {}),
      ...(values.role ? { role: values.role } : {}),
    });
  };

  const finalHeaders = useMemo(() => {
    if (mode === MODES.MERGING) {
      return [...DEFAULT_HEADERS, MERGE_LABEL];
    }
    return [...DEFAULT_HEADERS, VALIDATE_LABEL];
  }, [mode, MERGE_LABEL, VALIDATE_LABEL]);

  const resolveFieldLabel = useMemo(
    () => (fieldKey) => {
      const elem = configurationMap?.[fieldKey];
      return elem ? resolveConfigDisplayLabel(elem) : fieldKey;
    },
    [configurationMap],
  );

  const resolveSelectLabel = useMemo(
    () => (fieldKey, value, fallbackLabel) => {
      if (isOrganizationFieldKey(fieldKey)) {
        return formatOrganizationLabel(value);
      }
      const configField = configurationMap?.[fieldKey];
      const matchedOption = configField?.options?.find(
        (opt) => String(opt?.value) === String(value),
      );
      if (matchedOption) {
        return resolveOptionDisplayLabel(matchedOption) || matchedOption.label || String(value ?? '');
      }
      return fallbackLabel ?? String(value ?? '');
    },
    [configurationMap, formatOrganizationLabel],
  );

  const mergedColumnLabel = mode === MODES.MERGING ? MERGE_LABEL : VALIDATE_LABEL;

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <Box sx={mergeFormGridSx}>
        {finalHeaders?.map((heading) => (
          <Box key={heading}>
            <FormHeaderButton isMergedColumn={heading === mergedColumnLabel}>
              {heading}
            </FormHeaderButton>
          </Box>
        ))}
      </Box>

      <Box>
        <MergeFormFields
          key={mergeSectionKey}
          mergeState={mergeState}
          configuration={inputConfiguration}
          configurationMap={configurationMap}
          theme={theme}
          resolveFieldLabel={resolveFieldLabel}
          formatOrganizationLabel={formatOrganizationLabel}
          resolveSelectLabel={resolveSelectLabel}
          onSubmit={onSubmit}
        />
      </Box>
    </Paper>
  );
};
